import { defineStore } from 'pinia'
import type { VehicleDTO } from '@/modules/reservations/models/vehicle'
import type { ResponseResult } from '@/core/models/api-responses'
import vehicleService from '@/modules/reservations/services/vehicle-service'
import { VehicleFilters } from '../models/vehicle-filter'

interface VehicleState {
    vehicles: VehicleDTO[]
    availableVehicles: VehicleDTO[]
    selectedVehicle: VehicleDTO | null
    isLoading: boolean
    error: string | null
}

export const useVehicleStore = defineStore('vehicle', {
    state: (): VehicleState => ({
        vehicles: [],
        availableVehicles: [],
        selectedVehicle: null,
        isLoading: false,
        error: null
    }),

    getters: {
        getVehicleById: (state) => (id: string) => {
            return state.vehicles.find(vehicle => vehicle.id === id) || null
        },

        getAvailableVehicleById: (state) => (id: string) => {
            return state.availableVehicles.find(vehicle => vehicle.id === id) || null
        },

        hasAvailableVehicles: (state) => state.availableVehicles.length > 0,

        vehicleOptions: (state) => state.availableVehicles.map(vehicle => ({
            label: `${vehicle.brand} ${vehicle.model} (${vehicle.licensePlate}) - ${vehicle.numberOfSeats} lugares`,
            value: vehicle.id,
            vehicle: vehicle
        }))
    },

    actions: {
        /**
         * Fetch all vehicles without filters
         */
        async fetchAllVehicles(): Promise<void> {
            this.isLoading = true
            this.error = null

            try {
                const response: ResponseResult<VehicleDTO[]> = await vehicleService.getAll()

                if (response.isSuccess && response.data) {
                    this.vehicles = response.data
                } else {
                    this.error = response.error.message || 'Erro ao carregar veículos'
                    this.vehicles = []
                }
            } catch (error) {
                console.error('Error fetching vehicles:', error)
                this.error = 'Erro ao carregar veículos'
                this.vehicles = []
            } finally {
                this.isLoading = false
            }
        },

        /**
         * Fetch available vehicles based on filters (for reservation dates)
         */
        async fetchAvailableVehicles(filters?: VehicleFilters): Promise<void> {
            this.isLoading = true
            this.error = null

            try {
                let queryString = ''
                
                if (filters) {
                    const queryParams = new URLSearchParams()

                    if (filters.startDate !== undefined) {
                        // Convert timestamp to ISO string format
                        const startDate = new Date(filters.startDate).toISOString()
                        queryParams.append('startDate', startDate)
                    }
                    if (filters.endDate !== undefined) {
                        // Convert timestamp to ISO string format
                        const endDate = new Date(filters.endDate).toISOString()
                        queryParams.append('endDate', endDate)
                    }

                    queryString = queryParams.toString()
                }

                console.log('Vehicle API Query String:', queryString) // Debug log

                const response: ResponseResult<VehicleDTO[]> = await vehicleService.getAll(queryString)

                if (response.isSuccess && response.data) {
                    this.availableVehicles = response.data
                } else {
                    this.error = response.error.message || 'Erro ao carregar veículos disponíveis'
                    this.availableVehicles = []
                }
            } catch (error) {
                console.error('Error fetching available vehicles:', error)
                this.error = 'Erro ao carregar veículos disponíveis'
                this.availableVehicles = []
            } finally {
                this.isLoading = false
            }
        },

        /**
         * Fetch vehicle by ID
         */
        async fetchVehicleById(id: string): Promise<VehicleDTO | null> {
            this.isLoading = true
            this.error = null

            try {
                const response: ResponseResult<VehicleDTO> = await vehicleService.getById(id)

                if (response.isSuccess && response.data) {
                    this.selectedVehicle = response.data
                    return response.data
                } else {
                    this.error = response.error.message || 'Erro ao carregar veículo'
                    this.selectedVehicle = null
                    return null
                }
            } catch (error) {
                console.error('Error fetching vehicle by ID:', error)
                this.error = 'Erro ao carregar veículo'
                this.selectedVehicle = null
                return null
            } finally {
                this.isLoading = false
            }
        },

        /**
         * Set selected vehicle
         */
        setSelectedVehicle(vehicle: VehicleDTO | null): void {
            this.selectedVehicle = vehicle
        },

        /**
         * Clear available vehicles list
         */
        clearAvailableVehicles(): void {
            this.availableVehicles = []
        },

        /**
         * Clear selected vehicle
         */
        clearSelectedVehicle(): void {
            this.selectedVehicle = null
        },

        /**
         * Clear error state
         */
        clearError(): void {
            this.error = null
        },

        /**
         * Reset store state
         */
        resetStore(): void {
            this.vehicles = []
            this.availableVehicles = []
            this.selectedVehicle = null
            this.isLoading = false
            this.error = null
        }
    }
})